import type { ReactNode } from 'react';

export interface FatiaRosca {
  chave: string;
  rotulo: string;
  valor: number;
  /**
   * Passo da escala ORDINAL de azuis (styles.css, `ordem-1..3`): 1 e o mais
   * forte. A paleta nao tem cores categoricas, entao a rosca so serve para
   * categorias com ordem (em dia > vencida > nunca).
   */
  passo: 1 | 2 | 3;
  /** Texto de apoio na legenda (ex.: custo, prazo). O percentual ja vem. */
  sub?: string;
}

const COR: Record<FatiaRosca['passo'], string> = {
  1: 'var(--color-ordem-1)',
  2: 'var(--color-ordem-2)',
  3: 'var(--color-ordem-3)',
};
const RAIO = 58;
const VOLTA = 2 * Math.PI * RAIO;

/**
 * Rosca com legenda. A legenda e a tabela do grafico: rotulo, numero e
 * percentual de cada fatia, sempre escritos (identidade nunca so por cor). Um
 * vao de 3px separa as fatias. Com `foco`, a fatia escolhida fica cheia e as
 * outras recuam; com `onEscolher`, cada linha da legenda filtra a pagina.
 */
export function Rosca({
  fatias,
  rotulo,
  unidade,
  foco,
  onEscolher,
  vazio,
}: {
  fatias: readonly FatiaRosca[];
  rotulo: string;
  /** Embaixo do total, no centro (ex.: "animais"). */
  unidade: string;
  foco?: string | null;
  onEscolher?: (chave: string) => void;
  vazio?: ReactNode;
}) {
  const total = fatias.reduce((t, f) => t + f.valor, 0);
  if (total === 0)
    return <p className="px-2 py-8 text-center text-sm text-apagado">{vazio ?? 'Nada para repartir no recorte.'}</p>;

  const pct = (v: number) => `${Math.round((v / total) * 100)}%`;
  const vivas = fatias.filter((f) => f.valor > 0);
  const vao = vivas.length > 1 ? 3 : 0;
  let deslocamento = 0;
  const arcos = vivas.map((f) => {
    const comprimento = Math.max(0.5, (f.valor / total) * VOLTA - vao);
    const arco = { f, comprimento, deslocamento };
    deslocamento += (f.valor / total) * VOLTA;
    return arco;
  });
  const resumo = `${rotulo}: ${fatias.map((f) => `${f.rotulo} ${f.valor} (${pct(f.valor)})`).join(', ')}.`;

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4">
      <div className="relative size-36 shrink-0">
        <svg viewBox="0 0 150 150" role="img" aria-label={resumo} className="size-36 -rotate-90">
          <circle cx="75" cy="75" r={RAIO} fill="none" stroke="var(--color-trilha)" strokeWidth="18" />
          {arcos.map(({ f, comprimento, deslocamento }) => (
            <circle
              key={f.chave}
              cx="75"
              cy="75"
              r={RAIO}
              fill="none"
              stroke={COR[f.passo]}
              strokeWidth="18"
              strokeDasharray={`${comprimento.toFixed(2)} ${(VOLTA - comprimento).toFixed(2)}`}
              strokeDashoffset={(-deslocamento).toFixed(2)}
              opacity={foco && foco !== f.chave ? 0.3 : 1}
              className="transition-opacity duration-200"
            >
              <title>{`${f.rotulo}: ${f.valor} (${pct(f.valor)})`}</title>
            </circle>
          ))}
        </svg>
        <div aria-hidden className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <b className="numero text-2xl leading-tight font-bold tracking-tight">{total}</b>
          <span className="text-[11px] text-apagado">{unidade}</span>
        </div>
      </div>

      <ul className="flex min-w-44 flex-1 flex-col gap-1">
        {fatias.map((f) => {
          // Fora do foco so a amostra recua: texto apagado por opacidade perde contraste.
          const recua = !!foco && foco !== f.chave;
          const conteudo = (
            <>
              <span
                aria-hidden
                className={`size-2.5 shrink-0 self-center rounded-[3px] ${recua ? 'opacity-30' : ''}`}
                style={{ background: COR[f.passo] }}
              />
              <span className={`text-[13px] ${recua ? 'font-medium text-apagado' : 'font-semibold'}`}>{f.rotulo}</span>
              <span className="numero text-right text-[13px] font-bold">{f.valor}</span>
              <span className="col-start-2 col-end-4 -mt-0.5 text-[11.5px] text-apagado">
                {pct(f.valor)}
                {f.sub ? ` · ${f.sub}` : ''}
              </span>
            </>
          );
          const classe =
            'grid w-full grid-cols-[10px_minmax(0,1fr)_auto] items-baseline gap-x-2.5 rounded-[9px] px-2 py-1.5 text-left';
          return (
            <li key={f.chave}>
              {onEscolher ? (
                <button
                  type="button"
                  aria-pressed={foco === f.chave}
                  onClick={() => onEscolher(f.chave)}
                  title={`Filtrar: ${f.rotulo}`}
                  className={`${classe} min-h-0 hover:bg-superficie-2`}
                >
                  {conteudo}
                </button>
              ) : (
                <div className={classe}>{conteudo}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
